import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/* ── Icon definitions ─────────────────────────────────────────── */
const ITEMS = [
  {
    to: '/',
    label: 'Lobby',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    exact: true,
  },
  {
    to: '/game-modes',
    label: 'Modes',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <rect x="2" y="7" width="20" height="15" rx="2"/>
        <path d="M16 7V5a2 2 0 00-4 0v2M12 12v4M10 14h4"/>
      </svg>
    ),
  },
  {
    to: '/ui/dashboard',
    label: 'Dashboard',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <rect x="3" y="3" width="7" height="7" rx="1"/>
        <rect x="14" y="3" width="7" height="7" rx="1"/>
        <rect x="3" y="14" width="7" height="7" rx="1"/>
        <rect x="14" y="14" width="7" height="7" rx="1"/>
      </svg>
    ),
  },
  {
    to: '/ui/results',
    label: 'Results',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
      </svg>
    ),
  },
  {
    to: '/admincp/admin',
    label: 'Admin',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
      </svg>
    ),
  },
];

/* ── Desktop: 64px icon rail ──────────────────────────────────── */
function DesktopSidebar({ items }) {
  const location = useLocation();
  const [hoveredTo, setHoveredTo] = useState(null);

  return (
    <aside style={{
      position: 'fixed',
      top: 64,
      left: 0,
      bottom: 0,
      width: 64,
      zIndex: 90,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: 16,
      paddingBottom: 16,
      gap: 4,
      background: 'rgba(6, 11, 31, 0.85)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderRight: '1px solid rgba(255,255,255,0.07)',
    }}>
      {items.map(item => {
        const active = item.exact
          ? location.pathname === item.to
          : location.pathname.startsWith(item.to) && item.to !== '/';
        const isHome = item.to === '/';
        const isActive = isHome ? location.pathname === '/' || location.pathname === '/login' : active;
        const hovered = hoveredTo === item.to;

        return (
          <div key={item.to} style={{ position: 'relative', width: '100%' }}>
            {/* Tooltip */}
            {hovered && (
              <div style={{
                position: 'absolute',
                left: 70,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(13,19,50,0.95)',
                border: '1px solid rgba(0,212,255,0.25)',
                borderRadius: 8,
                padding: '5px 12px',
                color: '#fff',
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
                pointerEvents: 'none',
                zIndex: 999,
              }}>
                {item.label}
                {/* Arrow */}
                <div style={{
                  position: 'absolute',
                  left: -5,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0, height: 0,
                  borderTop: '5px solid transparent',
                  borderBottom: '5px solid transparent',
                  borderRight: '5px solid rgba(0,212,255,0.25)',
                }} />
              </div>
            )}

            <NavLink
              to={item.to}
              onMouseEnter={() => setHoveredTo(item.to)}
              onMouseLeave={() => setHoveredTo(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                borderRadius: 12,
                margin: '0 auto',
                color: isActive ? '#00d4ff' : 'rgba(255,255,255,0.45)',
                background: isActive ? 'rgba(0,212,255,0.12)' : hovered ? 'rgba(255,255,255,0.06)' : 'transparent',
                border: isActive ? '1px solid rgba(0,212,255,0.3)' : '1px solid transparent',
                boxShadow: isActive ? '0 0 12px rgba(0,212,255,0.2)' : 'none',
                transition: 'all 0.2s ease',
                textDecoration: 'none',
                position: 'relative',
              }}
            >
              {/* Active left bar */}
              {isActive && (
                <div style={{
                  position: 'absolute',
                  left: -10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 3,
                  height: 24,
                  borderRadius: '0 3px 3px 0',
                  background: '#00d4ff',
                  boxShadow: '0 0 8px rgba(0,212,255,0.8)',
                }} />
              )}
              {item.icon}
            </NavLink>
          </div>
        );
      })}

      {/* Bottom spacer + version */}
      <div style={{ flex: 1 }} />
      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.5px' }}>v2.0</div>
    </aside>
  );
}

/* ── Mobile: bottom navigation bar ───────────────────────────── */
function MobileNav({ items }) {
  const location = useLocation();

  return (
    <nav style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      minHeight: 64,
      height: 'calc(64px + env(safe-area-inset-bottom))',
      zIndex: 90,
      display: 'flex',
      alignItems: 'center',
      background: 'rgba(6, 11, 31, 0.95)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      borderTop: '1px solid rgba(255,255,255,0.07)',
      paddingLeft: 4,
      paddingRight: 4,
      paddingBottom: 'env(safe-area-inset-bottom)',
      boxSizing: 'border-box',
    }}>
      {items.map(item => {
        const isHome = item.to === '/';
        const isActive = isHome
          ? location.pathname === '/' || location.pathname === '/login'
          : location.pathname.startsWith(item.to) && item.to !== '/';

        return (
          <NavLink
            key={item.to}
            to={item.to}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              padding: '6px 0',
              color: isActive ? '#00d4ff' : 'rgba(255,255,255,0.4)',
              textDecoration: 'none',
              transition: 'color 0.2s',
              borderRadius: 10,
              background: isActive ? 'rgba(0,212,255,0.08)' : 'transparent',
              margin: '0 2px',
            }}
          >
            {item.icon}
            <span style={{ fontSize: 9, fontWeight: isActive ? 700 : 500, letterSpacing: '0.3px' }}>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

/* ── Export: conditional render by breakpoint ─────────────────── */
export default function Sidebar() {
  const { isAdmin } = useAuth();
  const visibleItems = ITEMS.filter(item => item.to !== '/admincp/admin' || isAdmin);
  return (
    <>
      <div className="hidden md:block">
        <DesktopSidebar items={visibleItems} />
      </div>
      <div className="md:hidden">
        <MobileNav items={visibleItems} />
      </div>
    </>
  );
}
